import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { trackBookingIntent } from "@/utils/analytics";

/**
 * Get current language from localStorage or path
 */
const getCurrentLanguage = (): string => {
  if (typeof window === "undefined") return "en";

  const savedLang = localStorage.getItem("preferred-language");
  if (savedLang) return savedLang;

  const pathParts = window.location.pathname.split("/").filter(Boolean);
  const langCode = pathParts[0];
  if (["de", "fr", "ja", "zh", "ko"].includes(langCode)) return langCode;

  return "en";
};

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",

        // Swiss Alpine Custom Variants
        alpine:
          "bg-primary text-primary-foreground hover:bg-accent transition-all duration-300 shadow-alpine hover:shadow-elegant hover:scale-105 font-medium",
        lake: "bg-secondary text-secondary-foreground hover:bg-pine-green transition-all duration-300 font-medium",
        elegant:
          "bg-elegant-white text-pine-green border border-edelweiss-gray hover:bg-pebble-beige transition-all duration-300 font-medium",
        hero: "bg-alpine-gold text-elegant-white hover:bg-alpine-gold active:bg-alpine-gold transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 font-semibold text-base",

        // Premium Wedding Theme Variants
        luxury:
          "bg-gradient-gold text-white hover:shadow-luxury transform hover:scale-105 transition-all duration-300 border-0",
        "elegant-wedding":
          "bg-white/10 text-white border border-white/20 hover:bg-white/20 backdrop-blur-sm transition-all duration-300",
        "ghost-light":
          "text-white hover:bg-white/10 border border-white/30 transition-all duration-300",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        xl: "h-14 rounded-lg px-10 text-lg",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  trackingType?: "cta" | "booking" | "contact_modal" | "none";
  trackingLabel?: string;
  trackingLocation?: string;
  trackingData?: {
    modalType?: "concierge" | "theatre" | "spa";
    parentModal?: string;
    roomType?: string;
  };
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      trackingType = "none",
      trackingLabel,
      trackingLocation,
      trackingData,
      onClick,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      // Track analytics based on tracking type
      if (trackingType === "booking") {
        trackBookingIntent(
          trackingData?.roomType,
          trackingLabel,
          trackingLocation,
        );
      } else if (
        trackingType === "contact_modal" &&
        trackingLabel &&
        trackingLocation
      ) {
        // Set button context variables for modal open event (modal tracks the open event)
        // No need to push button click event since modal open event already tracks this
        if (typeof window !== "undefined") {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            button_text: trackingLabel,
            button_location: trackingLocation,
            ...(trackingData?.parentModal && {
              parent_modal: trackingData.parentModal,
            }),
          });
        }
      }

      // Call original onClick if provided
      if (onClick) {
        onClick(e);
      }
    };

    // Add CSS class based on tracking type (prevents double-tracking in GTM)
    const trackingClass =
      trackingType === "booking"
        ? "booking-track"
        : trackingType === "contact_modal"
          ? "contact-modal-track"
          : "";

    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size, className }),
          trackingClass,
        )}
        ref={ref}
        onClick={handleClick}
        {...props}
        data-oid="8:-p36u"
      >
        {children}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
